import { yupResolver } from "@hookform/resolvers/yup";
import classnames from "classnames";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import styles from "@components/v1/buttons/WebinarRegistrationButton.module.css";
import DateAndTimeStamp from "@components/v1/dates/DateAndTimeStamp";
import FormButtons from "@components/v1/fields/FormButtons";
import Form from "@components/v1/forms/Form";
import BodyCopy from "@components/v1/typography/BodyCopy";
import Button from "@components/v2/buttons/Button";
import RadioGroup from "@components/v2/forms/RadioGroup";
import DividerRule from "@components/v2/graphic_elements/DividerRule";
import Modal from "@components/v2/overlays/Modal";
import Text from "@components/v2/typography/Text";
import useMountedTracking from "@hooks/useMountedTracking";
import useOpener, { Opener } from "@hooks/useOpener";
import useWebinarRegistration from "@hooks/useWebinarRegistration";
import { SortByTimestampDirectionEnum } from "@typing/Enums";
import {
  ArticleContentTypeEnum,
  ArticleFragment,
  WebinarRegistrationRecurrenceTypeEnum,
  WebinarRegistrationsDestroyMutation
} from "@typing/Generated";
import { sortByTimestamp, webinarRecurrenceDescription } from "@utils/modelUtils";
import { optionsForWebinarRegistrationRecurrenceType } from "@utils/optionUtils";

type Article = Pick<
  ArticleFragment,
  | "__typename"
  | "contentType"
  | "id"
  | "title"
  | "webinarCompleted"
  | "webinarOccurrenceRemainingSiblings"
  | "webinarRecurMonthlyDay"
  | "webinarRecurMonthlyType"
  | "webinarRecurMonthlyWeek"
  | "webinarRecurMonthlyWeekday"
  | "webinarRecurRepeatInterval"
  | "webinarRecurType"
  | "webinarRecurWeeklyDays"
  | "webinarRegistration"
  | "webinarStartTime"
>;

type RemainingWebinarRegistration = NonNullable<
  NonNullable<WebinarRegistrationsDestroyMutation["webinarRegistrationsDestroy"]>["registrationsRemainingInSeries"]
>[number];

type CreateModalProps = {
  article: Article;
  executeCreation: (recurrenceType: WebinarRegistrationRecurrenceTypeEnum) => Promise<void>;
  opener: Opener;
  showRegistrationSuccess: boolean;
};

type RecurrenceSelectionFormData = {
  recurrenceType: WebinarRegistrationRecurrenceTypeEnum;
};

const RecurringRegistrationCreateModal = ({
  article,
  executeCreation,
  opener,
  showRegistrationSuccess
}: CreateModalProps) => {
  const intl = useIntl();

  const schema: yup.ObjectSchema<RecurrenceSelectionFormData> = yup
    .object()
    .shape({
      recurrenceType: yup
        .string()
        .oneOf(Object.values(WebinarRegistrationRecurrenceTypeEnum))
        .required(intl.formatMessage({ id: "errors.webinarRegistration.recurrenceType.blank" }))
    })
    .required();

  const form = useForm<RecurrenceSelectionFormData>({
    defaultValues: { recurrenceType: WebinarRegistrationRecurrenceTypeEnum.ALL_FUTURE_OCCURRENCES },
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const handleSubmit = () => {
    const recurrenceType = form.getValues().recurrenceType;
    executeCreation(recurrenceType).then(() => {
      form.reset();
    });
  };

  return (
    <Modal opener={opener}>
      {() => (
        <div className={classnames("component-WebinarRegistrationButton", styles.wrapper)}>
          {!showRegistrationSuccess ? (
            <>
              <Text tag="h2" variant="2xl">
                {intl.formatMessage({ id: "pages.explore.events.completeRegistration" }, { title: article.title })}
              </Text>
              <div className={styles.eventDetails}>
                <span className={styles.eventTime}>
                  <DateAndTimeStamp showWeekday value={article.webinarStartTime} />
                </span>
                <span className={styles.dot} />
                <span>{webinarRecurrenceDescription(article, intl)}</span>
              </div>
              <DividerRule />
              <FormattedMessage id="pages.explore.events.joinInstructionsFromZoom" />
              <Form onSubmit={form.handleSubmit(handleSubmit)}>
                <RadioGroup<RecurrenceSelectionFormData>
                  control={form.control}
                  error={form.formState.errors.recurrenceType}
                  name="recurrenceType"
                  options={optionsForWebinarRegistrationRecurrenceType(intl)}
                />
                <FormButtons
                  alignment="right"
                  cancelKey="dictionary.cancel"
                  onCancel={() => {
                    opener.close();
                  }}
                  saveKey="pages.explore.events.submitRegistration"
                  stackOnSmallScreen
                />
              </Form>
            </>
          ) : (
            <div className={styles.successMessage}>
              <Text tag="h2" variant="2xl">
                {intl.formatMessage({ id: "pages.explore.events.registeredSuccess" })}
              </Text>
              <Text variant="md">{intl.formatMessage({ id: "pages.explore.events.checkEmail" })}</Text>
              <Button className={styles.closeButton} onClick={opener.close}>
                {intl.formatMessage({ id: "dictionary.close" })}
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

type DestroyModalProps = {
  executeRecurringDeletion: (webinarRegistrationId: string) => void;
  opener: Opener;
  registrationsRemainingToDelete: RemainingWebinarRegistration[] | undefined;
};

const RecurringRegistrationDestroyModal = ({
  executeRecurringDeletion,
  opener,
  registrationsRemainingToDelete
}: DestroyModalProps) => {
  const firstRegistrationToDelete =
    registrationsRemainingToDelete &&
    sortByTimestamp(
      registrationsRemainingToDelete,
      registration => registration.webinar.webinarStartTime,
      SortByTimestampDirectionEnum.CHRONOLOGICAL
    )[0];

  return (
    <Modal opener={opener}>
      {() => (
        <div className={classnames("component-WebinarRegistrationButton", styles.wrapper, styles.destroyMessage)}>
          {firstRegistrationToDelete ? (
            <>
              <h1>
                <FormattedMessage id="pages.explore.events.youHaveBeenRemoved" />
              </h1>
              <p>
                <BodyCopy>
                  <FormattedMessage
                    id="pages.explore.events.cancelFutureRegistrations"
                    values={{ count: registrationsRemainingToDelete.length }}
                  />
                </BodyCopy>
              </p>
              <Button inline onClick={opener.close}>
                <FormattedMessage id="pages.explore.events.noKeepFutureRegistrations" />
              </Button>
              <Button
                fill="clear"
                inline
                onClick={() => {
                  executeRecurringDeletion(firstRegistrationToDelete.id);
                }}
              >
                <FormattedMessage id="pages.explore.events.yesCancelFutureRegistrations" />
              </Button>
            </>
          ) : (
            <>
              <h1>
                <FormattedMessage id="pages.explore.events.youHaveBeenRemoved" />
              </h1>
              <Button inline onClick={opener.close}>
                <FormattedMessage id="dictionary.close" />
              </Button>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

type Props = {
  article: Article;
  journeyId: string | null;
};

const WebinarRegistrationButton = ({ article, journeyId }: Props) => {
  const [changingRegistration, setChangingRegistration] = useState<boolean>(false);
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState<boolean>(false);
  const isMounted = useMountedTracking();

  const [registrationsRemainingToDelete, setRegistrationsRemainingToDelete] =
    useState<RemainingWebinarRegistration[]>();

  const recurringRegistrationCreateOpener = useOpener();
  const recurringRegistrationDestroyOpener = useOpener();

  useEffect(() => {
    if (showRegistrationSuccess && !recurringRegistrationCreateOpener.isOpen) {
      setShowRegistrationSuccess(false);
    }
  }, [recurringRegistrationCreateOpener, showRegistrationSuccess]);

  const { createRegistrations, destroyRegistrations } = useWebinarRegistration();

  const executeCreation = (recurrenceType: WebinarRegistrationRecurrenceTypeEnum) => {
    setChangingRegistration(true);
    return createRegistrations({
      addAnalyticsEvent: false,
      article,
      journeyId,
      recurrenceType
    }).finally(() => {
      if (isMounted.current) {
        setShowRegistrationSuccess(true);
        setChangingRegistration(false);
      }
    });
  };

  const executeRecurringDeletion = (webinarRegistrationId: string) => {
    setChangingRegistration(true);
    destroyRegistrations({
      recurrenceType: WebinarRegistrationRecurrenceTypeEnum.ALL_FUTURE_OCCURRENCES,
      webinarRegistrationId
    }).finally(() => {
      if (isMounted.current) {
        recurringRegistrationDestroyOpener.close();
        setChangingRegistration(false);
      }
    });
  };

  const createRegistrationsCallback = () => {
    if (article.webinarOccurrenceRemainingSiblings > 0) {
      recurringRegistrationCreateOpener.open();
    } else {
      executeCreation(WebinarRegistrationRecurrenceTypeEnum.SINGLE_OCCURRENCE);
    }
  };

  const destroyRegistrationCallback = () => {
    if (article.webinarRegistration) {
      setChangingRegistration(true);
      destroyRegistrations({
        recurrenceType: WebinarRegistrationRecurrenceTypeEnum.SINGLE_OCCURRENCE,
        webinarRegistrationId: article.webinarRegistration.id
      })
        .then(data => {
          if (data?.webinarRegistrationsDestroy?.registrationsRemainingInSeries !== null) {
            if (
              data?.webinarRegistrationsDestroy &&
              data.webinarRegistrationsDestroy.registrationsRemainingInSeries.length > 0
            ) {
              setRegistrationsRemainingToDelete(data.webinarRegistrationsDestroy.registrationsRemainingInSeries);
              recurringRegistrationDestroyOpener.open();
            }
          }
        })
        .finally(() => {
          if (isMounted.current) setChangingRegistration(false);
        });
    }
  };

  if (article.contentType !== ArticleContentTypeEnum.WEBINAR || article.webinarCompleted) {
    return null;
  }

  return (
    <>
      {article.webinarRegistration ? (
        <Button color="gray-50" disabled={changingRegistration} inline onClick={destroyRegistrationCallback}>
          <FormattedMessage id="pages.explore.events.unregister" />
        </Button>
      ) : (
        <Button disabled={changingRegistration} inline onClick={createRegistrationsCallback}>
          <FormattedMessage id="pages.explore.events.register" />
        </Button>
      )}
      <RecurringRegistrationCreateModal
        article={article}
        executeCreation={executeCreation}
        opener={recurringRegistrationCreateOpener}
        showRegistrationSuccess={showRegistrationSuccess}
      />
      <RecurringRegistrationDestroyModal
        executeRecurringDeletion={executeRecurringDeletion}
        opener={recurringRegistrationDestroyOpener}
        registrationsRemainingToDelete={registrationsRemainingToDelete}
      />
    </>
  );
};

export default WebinarRegistrationButton;
